/**
 * Monitor de inactividad del usuario
 *
 * LÓGICA:
 * - Si el usuario no interactúa durante IDLE_LIMIT → cerrar sesión y pedir login
 * - Si el usuario está activo → resetear el contador
 * - "Activo" = cualquier movimiento de mouse, tecla, click, scroll, toque
 * - El contador se guarda en localStorage para sobrevivir F5
 *   (si vuelve después de 2h sin actividad → login directo)
 */

const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000 // 2 horas
const STORAGE_KEY   = 'pcrd_last_activity'
const EVENTS        = ['mousemove','mousedown','keydown','scroll','touchstart','click']

let timer: ReturnType<typeof setTimeout> | null = null
let onExpire: (() => void) | null = null

/** Registra actividad — resetea el countdown */
function touch() {
  localStorage.setItem(STORAGE_KEY, Date.now().toString())
  schedule()
}

/** Programa el próximo chequeo */
function schedule() {
  if (timer) clearTimeout(timer)
  timer = setTimeout(check, IDLE_LIMIT_MS)
}

/** Verifica si el tiempo de inactividad ya pasó */
function check() {
  const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? '0', 10)
  const elapsed = Date.now() - last
  if (elapsed >= IDLE_LIMIT_MS) {
    stop()
    onExpire?.()
  } else {
    // Todavía no — esperar el tiempo restante
    timer = setTimeout(check, IDLE_LIMIT_MS - elapsed)
  }
}

/** Inicia el monitor. Llama a `cb` cuando el usuario lleva IDLE_LIMIT sin actividad */
export function startInactivityMonitor(cb: () => void) {
  onExpire = cb

  // Chequear al iniciar — si ya pasaron 2h desde el último uso, expirar de inmediato
  const last = parseInt(localStorage.getItem(STORAGE_KEY) ?? '0', 10)
  if (last > 0 && Date.now() - last >= IDLE_LIMIT_MS) {
    // Sesión fría — expirar tras un pequeño delay para que el UI se monte
    setTimeout(cb, 500)
    return
  }

  // Registrar actividad inicial
  touch()

  // Escuchar eventos de actividad del usuario
  EVENTS.forEach(e => window.addEventListener(e, touch, { passive: true }))
}

/** Detiene el monitor (llamar en logout) */
export function stopInactivityMonitor() {
  if (timer) clearTimeout(timer)
  timer = null
  onExpire = null
  EVENTS.forEach(e => window.removeEventListener(e, touch))
  localStorage.removeItem(STORAGE_KEY)
}
